import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { useActiveCompany } from '../hooks/useActiveCompany';
import {
  Landmark, Save, Download, FileSpreadsheet, Eye,
  ArrowRight, Loader2, Settings, AlertCircle, Check,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const FinanceControls = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('mapping');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [period, setPeriod] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [mappings, setMappings] = useState([]);
  const [journal, setJournal] = useState(null);
  const [costSummary, setCostSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [erpType, setErpType] = useState('tally');

  useEffect(() => {
    axios.get(`${API_URL}/companies`, { withCredentials: true }).then(res => {
      setCompanies(res.data);
      setSelectedCompany((cur) => (res.data.length && !cur ? res.data[0].id : cur));
    });
  }, [setSelectedCompany]);

  const fetchTabData = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'mapping') {
        const { data } = await axios.get(`${API_URL}/finance/accounting-mappings`, { params: { company_id: selectedCompany }, withCredentials: true });
        setMappings(data);
      } else if (tab === 'journal') {
        const { data } = await axios.get(`${API_URL}/finance/journal-preview`, { params: { company_id: selectedCompany, period }, withCredentials: true });
        setJournal(data);
      } else if (tab === 'export') {
        const { data } = await axios.get(`${API_URL}/finance/cost-summary`, { params: { company_id: selectedCompany, period }, withCredentials: true });
        setCostSummary(data);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [period, selectedCompany, tab]);

  useEffect(() => {
    if (selectedCompany) fetchTabData();
  }, [tab, selectedCompany, period, fetchTabData]);


  const handleMappingChange = (index, field, value) => {
    const updated = [...mappings];
    updated[index] = { ...updated[index], [field]: value };
    setMappings(updated);
  };

  const handleSaveMappings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/finance/accounting-mappings`, {
        company_id: selectedCompany,
        mappings: mappings.map(m => ({
          salary_head_code: m.salary_head_code,
          salary_head_name: m.salary_head_name,
          gl_code: m.gl_code,
          gl_description: m.gl_description,
          debit_credit: m.debit_credit,
          cost_center: m.cost_center || '',
        })),
      }, { withCredentials: true });
      toast.success('Accounting mappings saved successfully');
    } catch (err) {
      toast.error('Failed to save mappings');
    } finally { setSaving(false); }
  };

  const handleErpExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/finance/erp-export`, {
        params: { company_id: selectedCompany, period, erp_type: erpType },
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${erpType}_export_${period}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${erpType.toUpperCase()} export downloaded`);
    } catch (err) {
      toast.error('Export failed - check if journal entries exist for this period');
    } finally { setExporting(false); }
  };

  const tabs = [
    { id: 'mapping', label: 'GL Mapping', icon: Settings },
    { id: 'journal', label: 'Journal Preview', icon: Eye },
    { id: 'export', label: 'ERP Export', icon: Download },
  ];

  const inr = (n) => n ? `INR ${n.toLocaleString('en-IN')}` : 'INR 0';

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="finance-controls-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Finance Controls</h1>
            <p className="mt-1 text-sm text-slate-500">Accounting integration, journal entries & ERP export</p>
          </div>
          <Landmark className="w-8 h-8 text-slate-400" />
        </div>

        {/* Controls */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap gap-4 items-end" data-testid="finance-controls-bar">
          <div className="min-w-[200px]">
            <label className="block text-xs font-medium text-slate-500 mb-1">Company</label>
            <select data-testid="finance-company-select" value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {tab !== 'mapping' && (
            <div className="min-w-[160px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Period</label>
              <input data-testid="finance-period-input" type="month" value={period} onChange={e => setPeriod(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
            </div>
          )}
        </div>

        {/* Tab Bar */}
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1" data-testid="finance-tabs">
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} data-testid={`finance-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            {/* ========== GL MAPPING ========== */}
            {tab === 'mapping' && (
              <div className="space-y-4" data-testid="finance-mapping-content">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-600">Map salary heads to General Ledger codes for accounting integration</p>
                  <button
                    data-testid="finance-save-mappings-btn"
                    onClick={handleSaveMappings}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium disabled:opacity-50 transition-colors"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save Mappings
                  </button>
                </div>
                <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
                  <table className="w-full text-xs" data-testid="finance-mapping-table">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-3 py-2.5 text-left text-slate-600 font-semibold">Salary Head Code</th>
                        <th className="px-3 py-2.5 text-left text-slate-600 font-semibold">Salary Head Name</th>
                        <th className="px-3 py-2.5 text-left text-slate-600 font-semibold">D/C</th>
                        <th className="px-3 py-2.5 text-left text-slate-600 font-semibold">GL Code</th>
                        <th className="px-3 py-2.5 text-left text-slate-600 font-semibold">GL Description</th>
                        <th className="px-3 py-2.5 text-left text-slate-600 font-semibold">Cost Center</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mappings.map((m, i) => (
                        <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-3 py-2">
                            <span className="font-mono font-medium text-slate-800">{m.salary_head_code}</span>
                          </td>
                          <td className="px-3 py-2 text-slate-700">{m.salary_head_name}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${m.debit_credit === 'debit' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>
                              {m.debit_credit?.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              data-testid={`mapping-gl-code-${i}`}
                              type="text"
                              value={m.gl_code || ''}
                              onChange={e => handleMappingChange(i, 'gl_code', e.target.value)}
                              placeholder="e.g. 4100"
                              className="w-full border border-slate-300 rounded px-2 py-1 text-xs font-mono focus:ring-1 focus:ring-blue-500"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              data-testid={`mapping-gl-desc-${i}`}
                              type="text"
                              value={m.gl_description || ''}
                              onChange={e => handleMappingChange(i, 'gl_description', e.target.value)}
                              placeholder="e.g. Salary Expense"
                              className="w-full border border-slate-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              data-testid={`mapping-cost-center-${i}`}
                              type="text"
                              value={m.cost_center || ''}
                              onChange={e => handleMappingChange(i, 'cost_center', e.target.value)}
                              placeholder="optional"
                              className="w-full border border-slate-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {mappings.some(m => !m.gl_code) && (
                  <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    <AlertCircle className="w-4 h-4" />
                    Some salary heads don't have GL codes mapped. Please complete mapping before generating journal entries.
                  </div>
                )}
              </div>
            )}

            {/* ========== JOURNAL PREVIEW ========== */}
            {tab === 'journal' && (
              <div className="space-y-4" data-testid="finance-journal-content">
                {journal && journal.entries?.length > 0 ? (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="flex gap-4 text-xs text-slate-600">
                        <span>Period: <strong>{journal.period}</strong></span>
                        <span>Headcount: <strong>{journal.headcount}</strong></span>
                        <span className={`flex items-center gap-1 ${journal.balanced ? 'text-green-600' : 'text-red-600'}`}>
                          {journal.balanced ? <Check className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                          {journal.balanced ? 'Balanced' : 'UNBALANCED'}
                        </span>
                      </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
                      <table className="w-full text-xs" data-testid="finance-journal-table">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-3 py-2.5 text-left text-slate-600 font-semibold">Head Code</th>
                            <th className="px-3 py-2.5 text-left text-slate-600 font-semibold">Account Name</th>
                            <th className="px-3 py-2.5 text-left text-slate-600 font-semibold">GL Code</th>
                            <th className="px-3 py-2.5 text-left text-slate-600 font-semibold">Type</th>
                            <th className="px-3 py-2.5 text-right text-slate-600 font-semibold">Debit (INR)</th>
                            <th className="px-3 py-2.5 text-right text-slate-600 font-semibold">Credit (INR)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {journal.entries.map((e, i) => (
                            <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="px-3 py-2 font-mono text-slate-800">{e.salary_head_code}</td>
                              <td className="px-3 py-2 text-slate-700">{e.salary_head_name}</td>
                              <td className="px-3 py-2 font-mono text-slate-600">{e.gl_code || '-'}</td>
                              <td className="px-3 py-2">
                                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                                  e.type === 'earning' ? 'bg-blue-50 text-blue-700' :
                                  e.type === 'employer_contribution' ? 'bg-purple-50 text-purple-700' :
                                  'bg-green-50 text-green-700'
                                }`}>{e.type}</span>
                              </td>
                              <td className="px-3 py-2 text-right font-mono text-blue-700">{e.debit > 0 ? e.debit.toLocaleString('en-IN') : ''}</td>
                              <td className="px-3 py-2 text-right font-mono text-green-700">{e.credit > 0 ? e.credit.toLocaleString('en-IN') : ''}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-slate-50 font-semibold border-t-2 border-slate-300">
                            <td colSpan={4} className="px-3 py-2.5 text-right text-slate-700">TOTAL</td>
                            <td className="px-3 py-2.5 text-right font-mono text-blue-800">{journal.total_debit?.toLocaleString('en-IN')}</td>
                            <td className="px-3 py-2.5 text-right font-mono text-green-800">{journal.total_credit?.toLocaleString('en-IN')}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
                    <Eye className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500">No journal entries for {period}</p>
                    <p className="text-xs text-slate-400 mt-1">Run payroll for this period to generate journal entries</p>
                  </div>
                )}
              </div>
            )}

            {/* ========== ERP EXPORT ========== */}
            {tab === 'export' && (
              <div className="space-y-6" data-testid="finance-export-content">
                {/* Cost Summary */}
                {costSummary?.summary && (
                  <div className="bg-white border border-slate-200 rounded-lg p-5">
                    <h3 className="font-semibold text-sm text-slate-800 mb-4">Payroll Cost Summary - {period}</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs" data-testid="finance-cost-summary-grid">
                      {Object.entries(costSummary.summary).map(([key, val]) => (
                        <div key={key} className="border border-slate-100 rounded-md p-3">
                          <p className="text-slate-500 capitalize">{key.replace(/total_|_/g, ' ').trim()}</p>
                          <p className="font-mono font-semibold text-slate-900 mt-1">{inr(val)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ERP Export Card */}
                <div className="bg-white border border-slate-200 rounded-lg p-5">
                  <h3 className="font-semibold text-sm text-slate-800 mb-4">Download ERP-Ready Export</h3>
                  <div className="flex flex-wrap gap-4 items-end">
                    <div className="min-w-[180px]">
                      <label className="block text-xs font-medium text-slate-500 mb-1">ERP Format</label>
                      <select data-testid="finance-erp-type-select" value={erpType} onChange={e => setErpType(e.target.value)}
                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                        <option value="tally">Tally (XML-compatible)</option>
                        <option value="sap">SAP FI Upload</option>
                        <option value="generic">Generic Journal</option>
                      </select>
                    </div>
                    <button
                      data-testid="finance-erp-export-btn"
                      onClick={handleErpExport}
                      disabled={exporting}
                      className="flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-medium disabled:opacity-50 transition-colors"
                    >
                      {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                      Download {erpType.toUpperCase()} Export
                    </button>
                  </div>
                  <div className="mt-4 flex flex-col gap-2 text-xs text-slate-500">
                    <div className="flex items-center gap-2">
                      <ArrowRight className="w-3 h-3" />
                      <span><strong>Tally:</strong> Voucher-format journal entries ready for Tally XML import</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ArrowRight className="w-3 h-3" />
                      <span><strong>SAP:</strong> FI posting format with document type, GL accounts, D/C indicators</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ArrowRight className="w-3 h-3" />
                      <span><strong>Generic:</strong> Standard journal entry format compatible with most ERP systems</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default FinanceControls;
